import { apiClient } from './api';

// EG-52 — Platform Admin quản lý khách hàng (User schema, collection `users`)
// cross-tenant. BE endpoint: /customers (CRUD).

export interface CustomerFilters {
  page?: number;
  limit?: number;
  tenant_id?: string;
  q?: string;
  state?: number;
}

export interface Customer {
  customer_id: string;
  tenant_id?: string;
  name: string;
  email?: string;
  phone?: string;
  state: number;
  source?: string;
  created_at: string;
  updated_at?: string;
}

export interface CreateCustomerDto {
  tenant_id: string;
  name: string;
  email?: string;
  phone?: string;
}

export interface UpdateCustomerDto {
  name?: string;
  email?: string;
  phone?: string;
  state?: number;
}

export interface CustomersResponse {
  data: Customer[];
  meta: { page: number; limit: number; total: number; totalPages: number };
}

interface Envelope<T> {
  success: boolean;
  data: T;
}

interface CustomerApiRaw {
  customer_id?: string;
  _id?: string;
  tenant_id?: string;
  name?: string;
  email?: string;
  phone?: string;
  state?: number;
  source?: string;
  created_at?: string;
  updated_at?: string;
}

const toCustomer = (raw: CustomerApiRaw): Customer => ({
  customer_id: raw.customer_id || raw._id || '',
  tenant_id: raw.tenant_id,
  name: raw.name || '',
  email: raw.email,
  phone: raw.phone,
  state: raw.state ?? 1,
  source: raw.source,
  created_at: raw.created_at || '',
  updated_at: raw.updated_at,
});

const unwrap = <T>(payload: Envelope<T> | T): T => {
  if (payload && typeof payload === 'object' && 'data' in (payload as Record<string, unknown>)) {
    return (payload as Envelope<T>).data;
  }
  return payload as T;
};

export const customerService = {
  getCustomers: async (filters: CustomerFilters = {}): Promise<CustomersResponse> => {
    const response = await apiClient.get<Envelope<{ items: CustomerApiRaw[]; meta: CustomersResponse['meta'] }>>(
      '/customers',
      { params: filters },
    );
    const payload = response.data?.data;
    return {
      data: (payload?.items || []).map(toCustomer),
      meta: payload?.meta || {
        page: filters.page || 1,
        limit: filters.limit || 20,
        total: 0,
        totalPages: 0,
      },
    };
  },

  getCustomerById: async (id: string): Promise<Customer> => {
    const response = await apiClient.get<Envelope<CustomerApiRaw>>(`/customers/${id}`);
    return toCustomer(response.data.data);
  },

  createCustomer: async (data: CreateCustomerDto): Promise<Customer> => {
    const response = await apiClient.post<Envelope<CustomerApiRaw> | CustomerApiRaw>('/customers', data);
    return toCustomer(unwrap(response.data));
  },

  updateCustomer: async (id: string, data: UpdateCustomerDto): Promise<Customer> => {
    const response = await apiClient.put<Envelope<CustomerApiRaw> | CustomerApiRaw>(`/customers/${id}`, data);
    return toCustomer(unwrap(response.data));
  },

  deleteCustomer: async (id: string): Promise<void> => {
    await apiClient.delete(`/customers/${id}`);
  },
};

export default customerService;
